import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createIndustrySpecialist as createIndustrySpecialistFn,
  updateIndustrySpecialist as updateIndustrySpecialistService,
  deleteIndustrySpecialist as deleteIndustrySpecialistService,
} from "../services/industry-specialist-service";
import { ApiError } from "pages/admin-app/shared/models/response/api-errors";
import { CreateIndustrySpecialistRequest } from "../models/request/create-industry-specialist-request";
import { CreateIndustrySpecialistResponse } from "../models/response/create-industry-specialist-response";
import { EditIndustrySpecialistResponse } from "../models/response/edit-industry-specialist-response";
import { AxiosError } from "axios";
import { EditIndustrySpecialistRequest } from "../models/request/edit-industry-specialist-request";

interface Props {
  id?: number | string;
  onEditSuccess?: () => void;
  onEditError?: () => void;
  onCreateSuccess?: () => void;
  onCreateError?: () => void;
}

export interface ApiErrorResponse {
  message: string;
  error: string;
  statusCode: number;
}

export default function useMutateIndustrySpecialist(props?: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: editIndustrySpecialist,
    error: editIndustrySpecialistError,
    isPending: editIndustrySpecialistLoading,
    isSuccess: editIndustrySpecialistSuccess,
  } = useMutation<
    EditIndustrySpecialistResponse,
    AxiosError<ApiErrorResponse>,
    EditIndustrySpecialistRequest
  >({
    mutationKey: ["industrySpecialist", props?.id, "edit"],
    mutationFn: updateIndustrySpecialistService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["industrySpecialist", props?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["industrySpecialists"] });
      props?.onEditSuccess && props?.onEditSuccess();
    },
    onError: () => {
      props?.onEditError && props?.onEditError();
    },
  });

  const {
    mutate: deleteIndustrySpecialist,
    error: deleteIndustrySpecialistError,
    isPending: deleteIndustrySpecialistLoading,
    reset: deleteIndustrySpecialistReset,
    isSuccess: deleteIndustrySpecialistSuccess,
    isError: deleteIndustrySpecialistIsError,
  } = useMutation({
    mutationKey: ["industrySpecialist", props?.id, "delete"],
    mutationFn: deleteIndustrySpecialistService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["industrySpecialists"] });
    },
  });

  const {
    mutate: createIndustrySpecialist,
    error: createIndustrySpecialistError,
    isPending: createIndustrySpecialistLoading,
    isSuccess: createIndustrySpecialistSuccess,
  } = useMutation<
    CreateIndustrySpecialistResponse,
    ApiError,
    CreateIndustrySpecialistRequest
  >({
    mutationKey: ["industrySpecialist", props?.id, "create"],
    mutationFn: createIndustrySpecialistFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["industrySpecialist"] });
      props?.onCreateSuccess && props?.onCreateSuccess();
    },
    onError: () => {
      props?.onCreateError && props?.onCreateError();
    },
  });

  return {
    editIndustrySpecialist,
    editIndustrySpecialistError,
    editIndustrySpecialistLoading,
    editIndustrySpecialistSuccess,

    deleteIndustrySpecialist,
    deleteIndustrySpecialistError,
    deleteIndustrySpecialistLoading,
    deleteIndustrySpecialistReset,
    deleteIndustrySpecialistSuccess,
    deleteIndustrySpecialistIsError,

    createIndustrySpecialist,
    createIndustrySpecialistError,
    createIndustrySpecialistLoading,
    createIndustrySpecialistSuccess,
  };
}
